import {ListView} from '@github-ui/list-view'
import {useAnalytics} from '@github-ui/use-analytics'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {GearIcon, StopIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Flash, IconButton} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {type PropsWithChildren, useId, useState} from 'react'
import type {STATUS_CHECK_ACCESSIBLE_NAMES} from '../../../helpers/status-check-helpers'
import {getAccessibleStatusText} from '../../../helpers/status-check-helpers'
import {useUpdateMergeBoxUserPreferenceMutation} from '../../../hooks/mutations/use-update-merge-box-user-preference'
import type {MergeBoxUserPreferences} from '../../../page-data/payloads/merge-box'
import {ExpandableGroupIcon} from '../common/ExpandableGroupIcon'
import expandedStyles from './ExpandedChecks.module.css'

import styles from './ChecksGroup.module.css'

export function ChecksGroup({
  children,
  count,
  groupStatus,
  isToggleVisible,
  analyticsMetadata,
  showSettingsIcon,
  setUserPreferences,
  pullRequestId,
  userPreferences,
}: PropsWithChildren<{
  count: number
  groupStatus: string
  analyticsMetadata: {statusCheckCounts: string}
  isToggleVisible?: boolean
  showSettingsIcon: boolean | null
  setUserPreferences?: (userPreferences: MergeBoxUserPreferences) => void
  pullRequestId: string
  userPreferences: MergeBoxUserPreferences | null
}>) {
  function getGroupText(groupStatusText: string): string {
    if (userPreferences?.statusChecksGrouping === 'ungrouped') {
      return count > 1 ? `${count} checks` : `${count} check`
    }

    return `${count} ${groupStatusText} check${count > 1 ? 's' : ''}`
  }

  const [isExpanded, setIsExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:checksGroup:${groupStatus}Expanded`,
    true,
  )
  const {sendAnalyticsEvent} = useAnalytics()
  const groupStatusText = getAccessibleStatusText(groupStatus as keyof typeof STATUS_CHECK_ACCESSIBLE_NAMES)
  const groupContentId = useId()
  const showListToggle = isToggleVisible
  const showChecksList = isExpanded || !isToggleVisible
  const groupText = getGroupText(groupStatusText)

  const shouldRenderChecks = !showListToggle || isExpanded

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const {mutate: updateMergeBoxUserPreference} = useUpdateMergeBoxUserPreferenceMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
    },
  })

  return (
    <div>
      {showListToggle && (
        <div className={expandedStyles.groupHeader}>
          {userPreferences?.statusChecksGrouping === 'ungrouped' ? (
            <span className={clsx(expandedStyles.totalChecksCountText, 'pl-2 text-semibold f6')}> {groupText} </span>
          ) : (
            <Button
              aria-controls={groupContentId}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? `Collapse ${groupText} group` : `Expand ${groupText} group`}
              className={expandedStyles.checksGroupHeadingButton}
              variant="invisible"
              size="small"
              trailingVisual={() => <ExpandableGroupIcon isExpanded={isExpanded} />}
              onClick={() => {
                const eventTarget = 'MERGEBOX_CHECKS_GROUP_TOGGLE_BUTTON'
                const eventType = isExpanded ? 'checks_group.collapse' : 'checks_group.expand'
                const eventMetadata = {...analyticsMetadata, group: groupStatus}
                sendAnalyticsEvent(eventType, eventTarget, eventMetadata)
                setIsExpanded(!isExpanded)
              }}
            >
              {groupText}
            </Button>
          )}
          {showSettingsIcon && setUserPreferences && (
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton
                  icon={GearIcon}
                  variant="invisible"
                  aria-label="Checks settings"
                  className={expandedStyles.checkSettingsButton}
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="auto">
                <ActionList selectionVariant="single">
                  <ActionList.Item
                    onSelect={() => {
                      if (userPreferences?.statusChecksGrouping !== 'grouped_by_status') {
                        updateMergeBoxUserPreference({
                          preferenceName: 'status_checks_grouping_preference',
                          preference: 'grouped_by_status',
                        })
                        setUserPreferences({...userPreferences, statusChecksGrouping: 'grouped_by_status'})
                      }
                    }}
                    selected={userPreferences?.statusChecksGrouping === 'grouped_by_status'}
                  >
                    Group by status
                  </ActionList.Item>
                  <ActionList.Item
                    onSelect={() => {
                      if (userPreferences?.statusChecksGrouping !== 'ungrouped') {
                        updateMergeBoxUserPreference({
                          preferenceName: 'status_checks_grouping_preference',
                          preference: 'ungrouped',
                        })
                        setUserPreferences({...userPreferences, statusChecksGrouping: 'ungrouped'})
                      }
                    }}
                    selected={userPreferences?.statusChecksGrouping === 'ungrouped'}
                  >
                    No grouping
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </div>
      )}
      {errorMessage && (
        <Flash variant="danger" className={styles.ErrorFlash}>
          <Octicon icon={StopIcon} className={styles.ErrorOcticon} />
          {errorMessage}
        </Flash>
      )}
      {shouldRenderChecks && (
        <div
          className={clsx(expandedStyles.expandableWrapper, showChecksList && expandedStyles.isExpanded)}
          id={groupContentId}
          aria-label={`${groupStatusText} checks`}
          role="group"
        >
          <div className={expandedStyles.expandableListView}>
            <ListView title={`${groupStatusText} checks`} titleHeaderTag="h3" strictFocusZone={false}>
              {children}
            </ListView>
          </div>
        </div>
      )}
    </div>
  )
}
